import {
  DASHBOARD_API_BASE_URL,
  USE_DASHBOARD_REAL_BACKEND,
  type DashboardApiResponse,
} from '../config/config';

import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth';

import type {
  DriverAvailability,
  DriverLanguage,
  DriverRecord,
  VehicleCategory,
} from '../../types';


export interface CreateDriverPayload {
  firstName: string;
  lastName: string;
  driverId?: string;
  dateOfBirth?: string;
  gender?: string;
  nationality?: string;
  experience?: string;
  languages?: string;
  mobileNumber: string;
  emailAddress?: string;
  emergencyContact?: string;
  homeAddress?: string;

  licenseNumber?: string;
  issueDate?: string;
  expiryDate?: string;

  driverPhoto?: File | string | null;
  drivingLicense?: File | string | null;
  passportId?: File | string | null;
  vehicleInsurance?: File | string | null;
  policeClearance?: File | string | null;
  medicalCertificate?: File | string | null;

  vehicleCategory?: VehicleCategory;
  vehiclePlate?: string;

  // Backend Vehicle ID
  vehicle?: number | string;
}

export interface DriverDetailsResponse {
  id: number | string;
  driver_id?: string;

  first_name?: string;
  last_name?: string;
  driver_name?: string;
  email?: string;
  mobile_number?: string;
  user_mobile_number?: string;

  date_of_birth?: string;
  gender?: string;
  nationality?: string;
  experience?: string;
  languages?: string;
  emergency_contact?: string;
  home_address?: string;

  license_number?: string;
  license_issue_date?: string;
  license_expiry_date?: string;

  vehicle?: number | string | {
    id?: number | string;
    name?: string;
    license_plate?: string;
  } | null;

  vehicle_name?: string;
  vehicle_plate?: string;

  documents?: Array<{
    id: number | string;
    document_type: string;
    document_type_display?: string;
    file: string;
    created_at?: string;
  }>;

  created_at?: string;
  updated_at?: string;
}

/**
 * Get authentication headers.
 *
 * IMPORTANT:
 * Do NOT set Content-Type here.
 * FormData automatically sets the correct multipart/form-data
 * content type including the boundary.
 */
const getAuthHeaders = (): HeadersInit => {
  const token =
    localStorage.getItem(ADMIN_TOKEN_KEY) ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token');

  if (
    !token ||
    token === 'demo_admin_token_active'
  ) {
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
};


/**
 * Normalize backend/mock driver data into DriverRecord.
 */
export const normalizeDriver = (
  raw: any,
  index: number = 0
): DriverRecord => {
  if (!raw || typeof raw !== 'object') {
    return {
      id: `driver-${index + 1}`,
      code: `DRV-${1000 + index}`,
      name: 'Unknown Driver',
      phone: '+43 660 000 0000',
      avatar: '',
      availability: 'available',
      vehicle: {
        brand: 'Unknown',
        category: 'Standard Saloon',
        plate: 'AT-000000',
      },
      incomeEur: 0,
      experienceYears: 0,
      language: 'English',
    };
  }

  const name =
    raw.name ||
    raw.driver_name ||
    [
      raw.firstName || raw.first_name,
      raw.lastName || raw.last_name,
    ]
      .filter(Boolean)
      .join(' ') ||
    'Unnamed Driver';

  const availability: DriverAvailability = [
    'available',
    'on-trip',
    'on-break',
    'offline',
  ].includes(raw.availability || raw.status)
    ? (raw.availability || raw.status)
    : 'available';

  const category: VehicleCategory =
    raw.vehicle?.category ||
    raw.vehicle_name ||
    raw.vehicle_category ||
    raw.vehicleType ||
    raw.vehicle_type ||
    raw.vehicleCategory ||
    'Standard Saloon';

  const plate =
    raw.vehicle?.plate ||
    raw.vehicle_plate ||
    raw.vehicle?.license_plate ||
    raw.plateNumber ||
    raw.plate_number ||
    raw.registrationNumber ||
    'AT-548796';
    const brand =
      raw.vehicle?.brand ||
      raw.vehicle_brand ||
      '';
  const language: DriverLanguage = [
    'English',
    'German',
    'English & German',
    'English & Italian',
  ].includes(raw.language || raw.languages)
    ? (raw.language || raw.languages)
    : 'English & German';

  const driverPhotoDocument = Array.isArray(raw.documents)
    ? raw.documents.find(
      (document: { document_type?: string; file?: string }) =>
        document.document_type === 'driver_photo' && Boolean(document.file)
    )
    : undefined;

  return {
    id: String(
      raw.id ||
      raw._id ||
      `driver-${index + 1}`
    ),

    code: String(
      raw.code ||
      raw.driverId ||
      raw.driver_id ||
      `DRV-${1000 + index}`
    ),

    name: String(name),
    user_email: String(
      raw.user_email ||
      raw.email ||
      ''
    ),
    phone: String(
      raw.phone ||
      raw.mobileNumber ||
      raw.mobile_number ||
      raw.user_mobile_number ||
      raw.contact ||
      'Phone number unavailable'
    ),

    avatar: String(
      raw.avatar ||
      raw.driverPhoto ||
      raw.photo ||
      driverPhotoDocument?.file ||
      ''
    ),

    availability,

    vehicle: {
      category,
      brand: String(brand),
      plate,
    },

    incomeEur: Number(
      raw.incomeEur ??
      raw.income_eur ??
      raw.income ??
      raw.total_income ??
      0
    ),

    experienceYears: Number(
      raw.experienceYears ??
      raw.experience_years ??
      (
        parseInt(
          String(raw.experience || '0'),
          10
        ) || 0
      )
    ),

    language,
  };
};


/**
 * Convert CreateDriverPayload into FormData.
 */
const buildDriverFormData = (
  payload: Partial<CreateDriverPayload>
): FormData => {
  const formData = new FormData();

  const appendValue = (
    key: string,
    value: unknown
  ) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== ''
    ) {
      formData.append(
        key,
        String(value)
      );
    }
  };


  // -----------------------------
  // Personal information
  // -----------------------------

  appendValue(
    'first_name',
    payload.firstName
  );

  appendValue(
    'last_name',
    payload.lastName
  );

  appendValue(
    'date_of_birth',
    payload.dateOfBirth
  );

  appendValue(
    'gender',
    payload.gender?.toLowerCase()
  );

  appendValue(
    'nationality',
    payload.nationality
  );

  appendValue(
    'experience',
    payload.experience
  );

  appendValue(
    'languages',
    payload.languages
  );

  appendValue(
    'mobile_number',
    payload.mobileNumber
  );

  appendValue(
    'email',
    payload.emailAddress
  );

  appendValue(
    'emergency_contact',
    payload.emergencyContact
  );

  appendValue(
    'home_address',
    payload.homeAddress
  );


  // -----------------------------
  // Driving license
  // -----------------------------

  appendValue(
    'license_number',
    payload.licenseNumber
  );

  appendValue(
    'license_issue_date',
    payload.issueDate
  );

  appendValue(
    'license_expiry_date',
    payload.expiryDate
  );


  // -----------------------------
  // Vehicle
  // -----------------------------

  if (
    payload.vehicle !== undefined &&
    payload.vehicle !== null
  ) {
    appendValue(
      'vehicle',
      payload.vehicle
    );
  }


  // -----------------------------
  // Uploaded documents
  // -----------------------------

  const appendFile = (
    key: string,
    file: File | string | null | undefined
  ) => {
    if (!file) {
      return;
    }

    if (file instanceof File) {
      formData.append(
        key,
        file
      );
    }
  };

  appendFile(
    'driverPhoto',
    payload.driverPhoto
  );

  appendFile(
    'drivingLicense',
    payload.drivingLicense
  );

  appendFile(
    'passportId',
    payload.passportId
  );

  appendFile(
    'vehicleInsurance',
    payload.vehicleInsurance
  );

  appendFile(
    'policeClearance',
    payload.policeClearance
  );

  appendFile(
    'medicalCertificate',
    payload.medicalCertificate
  );

  return formData;
};


/**
 * 1. Get all drivers
 *
 * GET /api/admin/drivers/
 */
export const apiGetDrivers = async (): Promise<
  DashboardApiResponse<DriverRecord[]>
> => {
  try {
    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/drivers/`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    const result =
      await response.json().catch(
        () => null
      );

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          result?.detail ||
          'Failed to fetch drivers.',
        data: [],
        warning:
          `Backend returned error ${response.status}.`,
      };
    }

    const list =
      Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : Array.isArray(result?.results)
            ? result.results
            : [];

    return {
      success: true,
      message:
        result?.message ||
        'Drivers loaded successfully.',
      data: list.map(
        (
          item: unknown,
          index: number
        ) =>
          normalizeDriver(
            item,
            index
          )
      ),
    };

  } catch (error) {

    console.error(
      'Get drivers connection error:',
      error
    );

    return {
      success: false,
      message: 'Connection failed.',
      warning:
        `Unable to reach backend at ${DASHBOARD_API_BASE_URL}.`,
      data: [],
    };
  }
};


/**
 * 2. Get one driver
 *
 * GET /api/admin/drivers/:id/
 */
export const apiGetDriverById = async (
  id: string
): Promise<
  DashboardApiResponse<DriverDetailsResponse>
> => {

  try {
    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/drivers/${id}/`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    const result =
      await response.json().catch(
        () => null
      );

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          result?.detail ||
          'Failed to load driver details.',
      };
    }

    const driverDetails =
      result?.data ?? result;

    if (
      !driverDetails ||
      typeof driverDetails !== 'object'
    ) {
      return {
        success: false,
        message:
          'Invalid driver details received from backend.',
      };
    }

    return {
      success: true,
      message:
        result?.message ||
        'Driver loaded successfully.',
      data:
        driverDetails as DriverDetailsResponse,
    };
  } catch (error) {
    console.error(
      'Get driver connection error:',
      error
    );

    return {
      success: false,
      message: 'Connection failed.',
      warning:
        `Unable to reach backend at ${DASHBOARD_API_BASE_URL}.`,
    };
  }
};


/**
 * 3. Create driver
 *
 * POST /api/admin/drivers/
 */
export const apiCreateDriver = async (
  payload: CreateDriverPayload
): Promise<
  DashboardApiResponse<DriverRecord>
> => {

  if (!USE_DASHBOARD_REAL_BACKEND) {

    const newRecord =
      normalizeDriver({
        id: `driver-${Date.now()}`,

        code:
          payload.driverId ||
          `DRV-${Math.floor(
            1000 +
            Math.random() * 9000
          )}`,

        name:
          `${payload.firstName} ${payload.lastName}`.trim(),

        phone:
          payload.mobileNumber,

        avatar:
          typeof payload.driverPhoto === 'string'
            ? payload.driverPhoto
            : '',

        availability:
          'available',

        vehicle: {
          category:
            payload.vehicleCategory ||
            'Standard Saloon',

          plate:
            payload.vehiclePlate ||
            'AT-548796',
        },

        incomeEur: 0,

        experienceYears:
          parseInt(
            payload.experience || '0',
            10
          ) || 0,

        language:
          (payload.languages as DriverLanguage) ||
          'English & German',
      });

    return {
      success: true,
      message:
        'Driver created successfully!',
      data: newRecord,
    };
  }

  try {

    const formData =
      buildDriverFormData(
        payload
      );

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/drivers/`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      }
    );

    const result =
      await response.json().catch(
        () => null
      );

    if (!response.ok) {

      console.error(
        'Create driver error:',
        result
      );    

      let errorMessage =
        result?.message ||
        result?.detail ||
        'Failed to create driver.';

      // Backend field-level validation errors
        const fieldErrors =
        result?.errors ??
        (
          result &&
          typeof result === 'object' &&
          !result.message &&
          !result.detail
            ? result
            : null
        );

      if (fieldErrors) {
        const errorDetails =
          Object.entries(fieldErrors)
            .map(([field, messages]) => {
              const message =
                Array.isArray(messages)
                  ? messages.join(', ')
                  : String(messages);

              return `${field}: ${message}`;
            })
            .join(' | ');

        if (errorDetails) {
          errorMessage =
            `${errorMessage} ${errorDetails}`;
        }
      }

      return {
        success: false,
        message: errorMessage,
      };
    }

    return {
      success: true,
      message:
        result?.message ||
        'Driver created successfully!',
      data: normalizeDriver(
        result?.data ?? result
      ),
    };

  } catch (error) {

    console.error(
      'Create driver connection error:',
      error
    );

    return {
      success: false,
      message: 'Connection failed.',
    };
  }
};


/**
 * 4. Update driver
 *
 * PATCH /api/admin/drivers/:id/
 */
export const apiUpdateDriver = async (
  id: string,
  payload: Partial<CreateDriverPayload>
): Promise<
  DashboardApiResponse<DriverRecord>
> => {
  if (!USE_DASHBOARD_REAL_BACKEND) {
    return {
      success: true,
      message:
        'Driver updated successfully!',
      data: normalizeDriver({
        id,
        ...payload,
      }),
    };
  }

  try {
    const formData =
      buildDriverFormData(payload);

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/drivers/${id}/`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: formData,
      }
    );

    const result =
      await response.json().catch(
        () => null
      );

    if (!response.ok) {
      console.error(
        'Update driver error:',
        result
      );

      let errorMessage =
        result?.message ||
        result?.detail ||
        `Failed to update driver. HTTP ${response.status}`;

      const fieldErrors =
        result?.errors ??
        (
          result &&
          typeof result === 'object' &&
          !result.message &&
          !result.detail
            ? result
            : null
        );

      if (fieldErrors) {
        const errorDetails =
          Object.entries(fieldErrors)
            .map(([field, messages]) => {
              const message =
                Array.isArray(messages)
                  ? messages.join(', ')
                  : String(messages);

              return `${field}: ${message}`;
            })
            .join(' | ');

        if (errorDetails) {
          errorMessage =
            `${errorMessage} ${errorDetails}`;
        }
      }

      return {
        success: false,
        message: errorMessage,
      };
    }

    const updatedDriver =
      result?.data ?? result;

    return {
      success: true,
      message:
        result?.message ||
        'Driver updated successfully!',
      data:
        normalizeDriver(updatedDriver),
    };
  } catch (error) {
    console.error(
      'Update driver connection error:',
      error
    );

    return {
      success: false,
      message: 'Connection failed.',
      warning:
        `Unable to reach backend at ${DASHBOARD_API_BASE_URL}.`,
    };
  }
};


/**
 * 5. Update driver availability
 *
 * NOTE:
 * The current backend Driver API does not yet expose
 * an availability endpoint, so this function should not
 * be used against the backend until that endpoint exists.
 */
export const apiUpdateDriverAvailability = async (
  _id: string,
  availability: DriverAvailability
): Promise<
  DashboardApiResponse<void>
> => {

  if (!USE_DASHBOARD_REAL_BACKEND) {
    return {
      success: true,
      message:
        `Availability set to ${availability}.`,
    };
  }

  return {
    success: false,
    message:
      'Driver availability API is not implemented yet.',
  };
};


/**
 * 6. Delete driver
 *
 * DELETE /api/admin/drivers/:id/
 */
export const apiDeleteDriver = async (
  id: string
): Promise<
  DashboardApiResponse<void>
> => {
  if (!USE_DASHBOARD_REAL_BACKEND) {
    return {
      success: true,
      message:
        'Driver deleted successfully.',
    };
  }

  try {
    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/drivers/${id}/`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }
    );

    let result: any = null;

    if (response.status !== 204) {
      result =
        await response.json().catch(
          () => null
        );
    }

    if (!response.ok) {
      console.error(
        'Delete driver error:',
        result
      );

      return {
        success: false,
        message:
          result?.message ||
          result?.detail ||
          `Failed to delete driver. HTTP ${response.status}`,
      };
    }

    return {
      success: true,
      message:
        result?.message ||
        'Driver deleted successfully.',
    };
  } catch (error) {
    console.error(
      'Delete driver connection error:',
      error
    );

    return {
      success: false,
      message: 'Connection failed.',
      warning:
        `Unable to reach backend at ${DASHBOARD_API_BASE_URL}.`,
    };
  }
};